import { createContext, useContext, useEffect, useMemo } from 'react';

import { getBrandConfig, type ResolvedBrandConfig } from './brand-config';

/** Compute the midpoint of two hex colors (used for intermediate shades like 350). */
function interpolateHex(a: string, b: string): string {
  const parse = (hex: string, start: number) =>
    parseInt(hex.slice(start, start + 2), 16);
  const r = Math.round((parse(a, 1) + parse(b, 1)) / 2);
  const g = Math.round((parse(a, 3) + parse(b, 3)) / 2);
  const bl = Math.round((parse(a, 5) + parse(b, 5)) / 2);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${bl.toString(16).padStart(2, '0')}`;
}

/** WCAG relative luminance of a hex color in [0, 1]. */
function relativeLuminance(hex: string): number {
  const channel = (start: number) => {
    const c = parseInt(hex.slice(start, start + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
}

const BrandContext = createContext<ResolvedBrandConfig | null>(null);

export function BrandProvider({
  clientName,
  children,
}: {
  clientName: string | null | undefined;
  children: React.ReactNode;
}) {
  const brand = useMemo(() => getBrandConfig(clientName), [clientName]);

  return (
    <BrandContext.Provider value={brand}>{children}</BrandContext.Provider>
  );
}

export const useBrand = () => {
  const ctx = useContext(BrandContext);
  if (ctx) return ctx;
  return getBrandConfig(undefined);
};

/**
 * Syncs brand primary color to CSS custom properties (--ui-green-*).
 * Call this once near the root of the app to keep tokens.css in sync
 * with the Chakra theme override.
 */
export function useBrandCssVars() {
  const brand = useBrand();

  useEffect(() => {
    const root = document.documentElement;
    const colorScale = brand.primaryColor;

    const green350 = interpolateHex(colorScale[300], colorScale[400]);
    root.style.setProperty('--ui-green-50', colorScale[50]);
    root.style.setProperty('--ui-green-100', colorScale[100]);
    root.style.setProperty('--ui-green-200', colorScale[200]);
    root.style.setProperty('--ui-green-300', colorScale[300]);
    root.style.setProperty('--ui-green-350', green350);
    root.style.setProperty('--ui-green-400', colorScale[400]);
    root.style.setProperty('--ui-green-500', colorScale[500]);
    root.style.setProperty('--ui-green-600', colorScale[600]);
    root.style.setProperty('--ui-green-700', colorScale[700]);
    root.style.setProperty('--ui-green-800', colorScale[800]);
    root.style.setProperty('--ui-green-900', colorScale[900]);

    // Contrast-aware text for elements painted on --ui-green-350 (e.g. the
    // selected state in NavigationButton). Use white on dark primaries,
    // navy on light primaries. 0.5 threshold works for both Revelio (light
    // green ~0.6) and Wilson (near-black ~0.01).
    root.style.setProperty(
      '--ui-on-primary-text',
      relativeLuminance(green350) < 0.5 ? '#ffffff' : '#2d426a'
    );

    // Legacy CSS custom properties
    root.style.setProperty('--primary-color', colorScale[500]);
    root.style.setProperty(
      '--deprecated-ui-surface-primary-normal',
      colorScale[500]
    );
    root.style.setProperty(
      '--deprecated-ui-border-color-primary-normal',
      colorScale[500]
    );

    // Sidebar color — only drives sidebar-scoped tokens so that
    // UI chrome using --ui-navy-blue-* stays fixed Revelio navy.
    const sidebarScale = brand.sidebarColor;
    root.style.setProperty('--ui-sidebar-50', sidebarScale[50]);
    root.style.setProperty('--ui-sidebar-100', sidebarScale[100]);
    root.style.setProperty('--ui-sidebar-200', sidebarScale[200]);
    root.style.setProperty('--ui-sidebar-300', sidebarScale[300]);
    root.style.setProperty('--ui-sidebar-400', sidebarScale[400]);
    root.style.setProperty('--ui-sidebar-500', sidebarScale[500]);
    root.style.setProperty('--ui-sidebar-600', sidebarScale[600]);
    root.style.setProperty('--ui-sidebar-700', sidebarScale[700]);
    root.style.setProperty('--ui-sidebar-800', sidebarScale[800]);
    root.style.setProperty('--ui-sidebar-900', sidebarScale[900]);

    // UI chrome text color — overrides the default --ui-text-* semantic
    // tokens so body copy, titles, and tooltip backgrounds follow the
    // brand's dark-neutral color instead of Revelio navy.
    const textScale = brand.textColor;
    root.style.setProperty('--ui-text-normal', textScale[500]);
    root.style.setProperty('--ui-text-hover', textScale[700]);
    root.style.setProperty('--ui-text-active', textScale[700]);
    root.style.setProperty('--ui-text-focus', textScale[800]);

    return () => {
      // Reset to defaults if brand changes back
      root.style.removeProperty('--ui-green-50');
      root.style.removeProperty('--ui-green-100');
      root.style.removeProperty('--ui-green-200');
      root.style.removeProperty('--ui-green-300');
      root.style.removeProperty('--ui-green-350');
      root.style.removeProperty('--ui-green-400');
      root.style.removeProperty('--ui-green-500');
      root.style.removeProperty('--ui-green-600');
      root.style.removeProperty('--ui-green-700');
      root.style.removeProperty('--ui-green-800');
      root.style.removeProperty('--ui-green-900');
      root.style.removeProperty('--ui-on-primary-text');
      root.style.removeProperty('--primary-color');
      root.style.removeProperty('--deprecated-ui-surface-primary-normal');
      root.style.removeProperty('--deprecated-ui-border-color-primary-normal');
      root.style.removeProperty('--ui-sidebar-50');
      root.style.removeProperty('--ui-sidebar-100');
      root.style.removeProperty('--ui-sidebar-200');
      root.style.removeProperty('--ui-sidebar-300');
      root.style.removeProperty('--ui-sidebar-400');
      root.style.removeProperty('--ui-sidebar-500');
      root.style.removeProperty('--ui-sidebar-600');
      root.style.removeProperty('--ui-sidebar-700');
      root.style.removeProperty('--ui-sidebar-800');
      root.style.removeProperty('--ui-sidebar-900');
      root.style.removeProperty('--ui-text-normal');
      root.style.removeProperty('--ui-text-hover');
      root.style.removeProperty('--ui-text-active');
      root.style.removeProperty('--ui-text-focus');
    };
  }, [brand.primaryColor, brand.sidebarColor, brand.textColor]);
}

/**
 * Sets document title and favicon based on brand config.
 * Call once in a top-level component.
 */
export function useBrandMeta() {
  const brand = useBrand();

  useEffect(() => {
    document.title = `${brand.displayName} Dashboard`;

    if (brand.logos.favicon) {
      const favicon = document.querySelector(
        'link[rel="icon"]'
      ) as HTMLLinkElement | null;
      if (favicon) {
        favicon.href = brand.logos.favicon;
      }
    }
  }, [brand.displayName, brand.logos.favicon]);
}
